import type { Country, MortgageTermsData } from '../types/mortgageTypes';
import { migrateCountrySwitch } from './canadianMortgageType';

export type LocaleInference = 'USA' | 'Canada' | 'ambiguous';

/** Set when IP refinement completes for an ambiguous locale (module scope). */
let ipRefinementCountry: Country | null = null;

/**
 * Sync locale signal: *-CA → Canada, *-US → USA, else ambiguous (may refine via IP).
 */
export function inferLocaleCountry(): LocaleInference {
  if (typeof navigator === 'undefined') return 'ambiguous';
  const list =
    navigator.languages?.length > 0 ? Array.from(navigator.languages) : [navigator.language];
  for (const raw of list) {
    if (!raw) continue;
    const tag = raw.trim();
    if (/-ca$/i.test(tag)) return 'Canada';
    if (/-us$/i.test(tag)) return 'USA';
  }
  return 'ambiguous';
}

/** Country used for initial Zustand state: Canada if locale says so, else USA (including ambiguous). */
export function getSyncDefaultCountryFromLocale(): Country {
  const locale = inferLocaleCountry();
  return locale === 'Canada' ? 'Canada' : 'USA';
}

/**
 * Best-effort country for presets and display: IP refinement wins when set; else sync locale default.
 */
export function getResolvedDefaultCountry(): Country {
  if (ipRefinementCountry !== null) return ipRefinementCountry;
  return getSyncDefaultCountryFromLocale();
}

export function setGeoDefaultFromIp(country: Country): void {
  ipRefinementCountry = country;
}

/** Clears IP refinement (for unit tests). */
export function resetGeoDefaultIpRefinement(): void {
  ipRefinementCountry = null;
}

export function isLocaleAmbiguous(): boolean {
  return inferLocaleCountry() === 'ambiguous';
}

/**
 * ipapi.co returns JSON with country_code (ISO 2). Returns null if unknown / error / non-US-CA.
 */
export async function fetchCountryFromIP(): Promise<Country | null> {
  const controller = new AbortController();
  const timeoutMs = 3500;
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch('https://ipapi.co/json/', {
      signal: controller.signal,
      credentials: 'omit',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { country_code?: string };
    const code = data.country_code?.toUpperCase();
    if (code === 'CA') return 'Canada';
    if (code === 'US') return 'USA';
    return null;
  } catch {
    return null;
  } finally {
    clearTimeout(id);
  }
}

/**
 * Apply geographic country to existing terms (matches MortgageTermsSection country switch + PMI defaults).
 */
export function mortgageTermsForCountryGeoSwitch(
  terms: MortgageTermsData,
  country: Country
): MortgageTermsData {
  const updated: MortgageTermsData = {
    ...terms,
    country,
    loanProgram: 'conventional',
    fhaDetails: undefined,
    vaDetails: undefined,
    usdaDetails: undefined,
    cmhcDetails: undefined,
  };
  const pmiApplies =
    updated.country === 'USA' &&
    updated.loanProgram === 'conventional' &&
    updated.downPaymentPercentage < 20;
  const pmi = { ...updated.pmi };
  if (pmiApplies && pmi.value === 0) {
    pmi.value = 0.8;
  } else if (!pmiApplies && pmi.value > 0) {
    pmi.value = 0;
  }
  updated.pmi = pmi;

  return migrateCountrySwitch(updated, country);
}
